import { OptionalId, SimpleCard, Sortable } from '@ygtechnologies/bygr-frontend-component-library';
import React, { useEffect, useMemo, useState } from 'react';
import { LinkingType, OptionGroupDto, RoomDto } from '@/types';
import { createQueryHook, useGetProjectId } from '@/utils/hooks';
import { searchOptionGroups } from '@/services/options/option-groups';
import { useReorderOptionGroupsByRoom } from '@/services/options/option-groups.hooks';
import { OptionGroupAccordion } from '@/seller/components/options/OptionGroupAccordion/OptionGroupAccordion';
interface RoomOptionCardProps {
    room: OptionalId<RoomDto>;
}

import theme from './RoomInfo.module.scss';
import { Trans } from '@lingui/react/macro';
import { arrayMove } from '@ygtechnologies/bygr-frontend-component-library';
import { faGripDotsVertical } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { ReorderPopupBar } from '@/components/ReorderPopupBar/ReorderPopupBar';
import orderBy from 'lodash/orderBy';

export const RoomOptionCard = ({ room }: RoomOptionCardProps) => {
    const projectId = useGetProjectId();
    const { data: options, refetch } = createQueryHook(searchOptionGroups)({ projectId: projectId!, rooms: [room.id!] });
    const [reorderedOptions, setReorderedOptions] = useState<OptionGroupDto[] | null>(null);
    const reorderMutation = useReorderOptionGroupsByRoom();

    useEffect(() => {
        setReorderedOptions(null);
    }, [room.id]);
    const sortedOptions = useMemo(() => {
        return orderBy(
            options,
            [
                (option) => {
                    if (option.linkType !== LinkingType.Room) {
                        return Number.MAX_SAFE_INTEGER;
                    }
                    const link = option.linkedRooms.find((roomLink) => roomLink.roomId === room.id);
                    return link?.optionGroupSortOrder ?? Number.MAX_SAFE_INTEGER;
                },
                (option) => option.id,
            ],
            ['asc', 'asc']
        );
    }, [options, room.id]);

    const displayOptions = reorderedOptions ?? sortedOptions;

    const onSortEnd = ({ oldIndex, newIndex }) => {
        const newOptions = arrayMove(displayOptions, oldIndex, newIndex);
        setReorderedOptions(newOptions);
    };

    const cancelReorder = () => {
        setReorderedOptions(null);
    };

    const acceptReorder = async () => {
        if (!room.id || !reorderedOptions) return;
        await reorderMutation.mutateAsync({
            roomId: room.id,
            sortedOptionGroupIds: reorderedOptions.map((option) => option.id),
        });
        await refetch();
        setReorderedOptions(null);
    };

    if (!options) return null;

    return (
        <SimpleCard className={theme.optionCard}>
            {displayOptions.length ? (
                <Sortable items={displayOptions} idField="id" onSortEnd={onSortEnd} direction="vertical">
                    {(option, { handleProps }) => (
                        <div className={theme.optionGroupRow} key={option.id}>
                            <div className={theme.optionGroupHandle} {...handleProps}>
                                <FontAwesomeIcon icon={faGripDotsVertical} size="lg" />
                            </div>
                            <div className={theme.optionGroupContent}>
                                <OptionGroupAccordion optionGroup={option} readonly />
                            </div>
                        </div>
                    )}
                </Sortable>
            ) : (
                <Trans>This room is not linked to any option groups. Add this room to an option group to see the options.</Trans>
            )}
            <ReorderPopupBar open={!!reorderedOptions} onCancel={cancelReorder} onAccept={acceptReorder} />
        </SimpleCard>
    );
};
